<template>
  <div class="container px-0 mb-2" id="production-strategy">
    <CardRoot class="border-translucent">
      <CardHeader>
        <CardTitle>{{ $t("production_strategy.title") }}</CardTitle>
      </CardHeader>
      <CardBody class="pt-0">
        <TabsContainer
          :tabs="tabs"
          @change:tab="setTab"
          :activeTab="activeTab || 'overview'"
        >
          <template #tab-overview>
            <div class="production-strategy-overview">
              <LoadingOverlayComponent v-if="isLoading" />

              <!-- A refresh after a card's edit keeps the cards mounted under the overlay: the
                   forward card's expanded mandates panel holds its filters. -->
              <div v-if="!isInitialLoad">
                <!-- Production Header -->
                <div class="row mb-4">
                  <div class="col-12">
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <h3 class="mb-0">
                        ~{{ formattedTotalProduction }} {{ $t("common.mwh") }}
                      </h3>
                      <div class="d-flex gap-3 align-items-center">
                        <!-- Chart View Toggle (only in multi-company mode) -->
                        <div
                          v-if="!isSingleCompany"
                          class="btn-group"
                          role="group"
                        >
                          <button
                            type="button"
                            :class="[
                              'btn',
                              'btn-sm',
                              chartViewMode === 'allocations'
                                ? 'btn-primary'
                                : 'btn-outline-primary',
                            ]"
                            @click="chartViewMode = 'allocations'"
                          >
                            {{
                              $t("production_strategy.production_allocations")
                            }}
                          </button>
                          <button
                            type="button"
                            :class="[
                              'btn',
                              'btn-sm',
                              chartViewMode === 'companies'
                                ? 'btn-primary'
                                : 'btn-outline-primary',
                            ]"
                            @click="chartViewMode = 'companies'"
                          >
                            {{ $t("production_strategy.company_production") }}
                          </button>
                        </div>

                        <!-- Time Period Toggle -->
                        <div class="btn-group" role="group">
                          <button
                            type="button"
                            :class="[
                              'btn',
                              'btn-sm',
                              viewMode === 'next-12-months'
                                ? 'btn-primary'
                                : 'btn-outline-primary',
                            ]"
                            @click="viewMode = 'next-12-months'"
                          >
                            {{ $t("production_strategy.whole_year") }}
                          </button>
                          <button
                            type="button"
                            :class="[
                              'btn',
                              'btn-sm',
                              viewMode === 'next-year'
                                ? 'btn-primary'
                                : 'btn-outline-primary',
                            ]"
                            @click="viewMode = 'next-year'"
                          >
                            {{ $t("production_strategy.next_year") }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Production Allocation Chart -->
                <div class="row mb-4">
                  <div class="col-12">
                    <ProductionAllocationChart
                      :chartData="chartData"
                      :allocations="allocations"
                      :chartViewMode="chartViewMode"
                      :companies="companies"
                      :companyResults="filteredCompanyResults"
                    />
                  </div>
                </div>

                <!-- Yearly Allocations Section -->
                <div class="row">
                  <div class="col-12">
                    <h4 class="mb-3 font-serif">
                      {{ $t("production_strategy.yearly_allocations") }}
                    </h4>
                  </div>
                </div>

                <!-- Allocation Cards - Single Column -->
                <div class="row g-3">
                  <!-- PPA Card -->
                  <div
                    v-if="
                      isSingleCompany
                        ? currentCompany?.feature_flags?.ppa_allocation
                        : anyCompanyHasPPAAllocation
                    "
                    class="col-12"
                  >
                    <PPAAllocationCard
                      :allocation="allocations.ppa"
                      :companies="companies"
                      :portfolios="portfolios"
                      :registryDevices="registryDevices"
                      :individualAllocations="
                        allocations.ppa.individual_allocations
                      "
                      @success="handleAllocationSuccess"
                    />
                  </div>

                  <!-- Forward Sales Card -->
                  <div
                    v-if="
                      isSingleCompany
                        ? currentCompany?.feature_flags?.forward_sales_enabled
                        : anyCompanyHasForwardSales
                    "
                    class="col-12"
                  >
                    <ForwardAllocationCard
                      :allocation="allocations.forward"
                      :companies
                      @updated="refreshData"
                    />
                  </div>

                  <!-- Self-managed Card -->
                  <div
                    v-if="
                      isSingleCompany
                        ? currentCompany?.feature_flags?.hold_allocation
                        : anyCompanyHasHoldAllocation
                    "
                    class="col-12"
                  >
                    <SelfManagedAllocationCard
                      :allocation="allocations.selfManaged"
                      :companies="companies"
                      :registryDevices="registryDevices"
                      @updated="refreshData"
                    />
                  </div>

                  <!-- Spot Sales Card -->
                  <div class="col-12">
                    <SpotAllocationCard
                      :allocation="allocations.spot"
                      :companies="companies"
                      @success="handleAllocationSuccess"
                    />
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template #tab-issuance-report>
            <IssuanceReportingTab
              v-if="currentCompany"
              :company="currentCompany"
              :registryDevices="registryDevices"
            />
            <div v-else class="text-center py-4">
              <p class="text-muted">
                {{ $t("production_strategy.not_available_consolidated") }}
              </p>
            </div>
          </template>
          <template #tab-devices>
            <!-- Single company mode -->
            <div v-if="isSingleCompany">
              <RegistryDevicesSection
                v-if="
                  currentCompany &&
                  (currentCompany.country === 'PL' ||
                    (registryDevices && registryDevices.length > 0))
                "
                :company="currentCompany"
              />
              <PlaceholderCardComponent
                v-else
                :title="$t('dashboard.companyData.no_devices')"
                :subtitle="$t('dashboard.companyData.add_device_message')"
              >
              </PlaceholderCardComponent>
              <div class="mt-3">
                <button class="btn btn-primary" @click="goToRegisterNewDevice">
                  <font-awesome-icon icon="plus" class="me-1" />
                  {{ $t("dashboard.add_company_or_device") }}
                </button>
              </div>
            </div>

            <!-- Multi-company mode -->
            <div v-else>
              <ProductionDevicesCard
                :companies="companiesWithIds"
                :registry-devices="registryDevices"
              />
            </div>
          </template>
        </TabsContainer>
      </CardBody>
    </CardRoot>

    <!-- Loading overlay during portfolio operations -->
    <LoadingOverlayComponent v-if="productionStore.isLoadingPortfolios" />
  </div>
</template>

<script setup lang="ts" name="ProductionStrategy">
import {
  CardBody,
  CardHeader,
  CardRoot,
  CardTitle,
} from "@/components/common/card";
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import PlaceholderCardComponent from "@/components/common/PlaceholderCardComponent.vue";
import TabsContainer from "@/components/common/tabs/TabsContainer.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { useTabs } from "@/composables/useTabs";
import ProductionDevicesCard from "@/dashboard/consolidated/ProductionDevicesCard.vue";
import type { Company, RegistryDevice } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { filter } from "lodash-es";
import { computed, onMounted, ref, toRef } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import IssuanceReportingTab from "./issuance-reporting/IssuanceReportingTab.vue";
import ForwardAllocationCard from "./overview/forwards/ForwardAllocationCard.vue";
import PPAAllocationCard from "./overview/PPA/PPAAllocationCard.vue";
import ProductionAllocationChart from "./overview/ProductionAllocationChart.vue";
import SelfManagedAllocationCard from "./overview/self-managed/SelfManagedAllocationCard.vue";
import SpotAllocationCard from "./overview/spot/SpotAllocationCard.vue";
import { useProductionAllocationData } from "./overview/useProductionAllocationData";
import RegistryDevicesSection from "./RegistryDevicesSection.vue";
import { useProductionStrategyStore } from "./useProductionStrategyStore";

const props = defineProps<{
  companies: Company[];
  registryDevices: RegistryDevice[];
}>();

const { t } = useI18n();
const router = useRouter();
const productionStore = useProductionStrategyStore();
const { globalErrorHandler } = useGlobalHandlers();

// Multi-company mode detection
const isSingleCompany = computed(() => props.companies.length === 1);
const currentCompany = computed(() =>
  isSingleCompany.value ? props.companies[0] : null
);

// Feature flag checks for multi-company mode
const anyCompanyHasPPAAllocation = computed(() => {
  return props.companies.some(
    (company) => company.feature_flags?.ppa_allocation === true
  );
});

const anyCompanyHasHoldAllocation = computed(() => {
  return props.companies.some(
    (company) => company.feature_flags?.hold_allocation === true
  );
});

const anyCompanyHasForwardSales = computed(() => {
  return props.companies.some(
    (company) => company.feature_flags?.forward_sales_enabled === true
  );
});

// Tab configuration
const tabs = computed(() => {
  const availableTabs = [];

  availableTabs.push({
    id: "overview",
    name: t("production_strategy.overview"),
    icon: "chart-line",
  });

  // Only show issuance report tab for single company view
  if (isSingleCompany.value) {
    availableTabs.push({
      id: "issuance-report",
      name: t("tradingCenter.tabs.issuance_reporting.title"),
      icon: "file-invoice-dollar",
    });
  }

  availableTabs.push({
    id: "devices",
    name: t("common.devices"),
    icon: "solar-panel",
  });

  return availableTabs;
});

const { activeTab, setTab } = useTabs("production-strategy", {
  defaultTabId: "overview",
  tabs: tabs,
});

// View mode for chart
const viewMode = ref<"next-12-months" | "next-year">("next-12-months");
const chartViewMode = ref<"allocations" | "companies">("allocations");

// Use the production allocation data composable
const {
  isLoading,
  isInitialLoad,
  allocations,
  chartData,
  totalProduction,
  refreshData,
  filteredCompanyResults,
} = useProductionAllocationData(
  toRef(props, "companies"),
  viewMode,
  toRef(props, "registryDevices")
);

// Computed properties
const formattedTotalProduction = computed(() => {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(totalProduction.value);
});

const companiesWithIds = computed(() => {
  return filter(
    props.companies,
    (company): company is Company & { id: number } =>
      typeof company.id === "number"
  );
});

// Computed properties from store
const portfolios = computed(() => productionStore.portfolios as any);

const handleAllocationSuccess = async (_message: string) => {
  try {
    await productionStore.refreshMultiCompanyData(props.companies);
    await refreshData();
  } catch (error) {
    globalErrorHandler(error);
  }
};

// Load data on component mount
// This prevents multiple re-renders and chart bouncing by loading all data once
onMounted(async () => {
  if (props.companies.length > 0) {
    try {
      await Promise.all([
        productionStore.refreshMultiCompanyData(props.companies),
        refreshData(),
      ]);
    } catch (error) {
      globalErrorHandler(error);
    }
  }
});

const goToRegisterNewDevice = () => {
  router.push({ name: "ProducerSignUp" });
};
</script>

<style scoped lang="scss"></style>
